import React, { useState } from "react";
import { Link } from "react-router-dom";
import { submitContactForm, submitDemoForm } from "../lib/contactApi";
import Seo from "../components/Seo";
import { ROUTE_META, SITE_URL, CONTACT_EMAIL, breadcrumbSchema } from "../seo/siteMeta.mjs";

const contactSchema = [
  {
    "@type": "ContactPage",
    name: ROUTE_META["/contact-us"].title,
    description: ROUTE_META["/contact-us"].description,
    url: `${SITE_URL}/contact-us`,
    mainEntity: { "@id": `${SITE_URL}/#organization` },
  },
  {
    "@type": "ContactPoint",
    contactType: "sales",
    email: CONTACT_EMAIL,
    availableLanguage: ["English"],
    areaServed: "Worldwide",
  },
  breadcrumbSchema([
    { name: "Home", path: "/" },
    { name: "Contact", path: "/contact-us" },
  ]),
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const topics = [
  "Risk Management",
  "Dark Web Monitoring",
  "AI Automation",
  "Frameworks",
  "Automated Compliance",
  "Enterprise GRC",
];
const headcounts = ["1 - 50", "51 - 200", "201 - 500", "501 - 1,000", "1,001 - 5,000", "5,000+"];
const countries = [
  "United States", "Canada", "United Kingdom", "Ireland", "Australia", "New Zealand",
  "Germany", "France", "Netherlands", "Spain", "Italy", "Sweden", "Norway", "Denmark",
  "Switzerland", "Poland", "India", "Singapore", "Japan", "South Korea", "Brazil",
  "Mexico", "South Africa", "United Arab Emirates", "Other",
];
const helpOptions = topics;

const Field = ({ id, label, required, error, children, hint }) => (
  <div className={`field${error ? " is-invalid" : ""}`}>
    <label htmlFor={id}>
      {label} {required && <span className="req" aria-hidden="true">*</span>}
    </label>
    {children}
    {hint}
    <p className="field-error" role="alert">{error}</p>
  </div>
);

const useForm = (initial, validators) => {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null); // {type, msg}
  const [submitting, setSubmitting] = useState(false);

  const set = (name, value) =>
    setValues((v) => ({ ...v, [name]: value }));

  const reset = () => setValues(initial);

  const validate = () => {
    const next = {};
    Object.entries(validators).forEach(([name, fn]) => {
      const msg = fn(values[name], values);
      if (msg) next[name] = msg;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  return { values, errors, status, setStatus, set, validate, submitting, setSubmitting, reset };
};

const ContactForm = () => {
  const f = useForm(
    { fullName: "", company: "", email: "", phone: "", topic: "", message: "" },
    {
      fullName: (v) => (!v.trim() ? "Please enter your full name." : ""),
      email: (v) => (!v.trim() ? "Please enter your email." : !EMAIL_RE.test(v) ? "Enter a valid email address." : ""),
      topic: (v) => (!v ? "Please choose a topic." : ""),
      message: (v) => (!v.trim() ? "Please enter a message." : ""),
    }
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!f.validate()) {
      f.setStatus({ type: "error", msg: "Please fix the highlighted fields and try again." });
      return;
    }
    f.setSubmitting(true);
    f.setStatus(null);
    try {
      await submitContactForm(f.values);
      f.setStatus({ type: "ok", msg: "Thanks! Your message has been sent. We'll be in touch shortly." });
      f.reset();
    } catch (err) {
      f.setStatus({ type: "error", msg: err.message || "Something went wrong. Please try again or email info@darkdefensive.com." });
    } finally {
      f.setSubmitting(false);
    }
  };

  return (
    <form className="form-panel" noValidate onSubmit={onSubmit}>
      {f.status && (
        <div className={`form-status ${f.status.type}`} role="alert">{f.status.msg}</div>
      )}
      <div className="form-row">
        <Field id="contactName" label="Full Name" required error={f.errors.fullName}>
          <input id="contactName" type="text" placeholder="Jane Doe" autoComplete="name"
            value={f.values.fullName} onChange={(e) => f.set("fullName", e.target.value)} />
        </Field>
        <Field id="contactCompany" label="Company">
          <input id="contactCompany" type="text" placeholder="Your Organization" autoComplete="organization"
            value={f.values.company} onChange={(e) => f.set("company", e.target.value)} />
        </Field>
        <Field id="contactEmail" label="Email" required error={f.errors.email}>
          <input id="contactEmail" type="email" placeholder="you@company.com" autoComplete="email"
            value={f.values.email} onChange={(e) => f.set("email", e.target.value)} />
        </Field>
        <Field id="contactPhone" label="Phone">
          <input id="contactPhone" type="tel" placeholder="Optional" autoComplete="tel"
            value={f.values.phone} onChange={(e) => f.set("phone", e.target.value)} />
        </Field>
      </div>

      <Field id="contactTopic" label="I'm Interested In" required error={f.errors.topic}>
        <select id="contactTopic" value={f.values.topic} onChange={(e) => f.set("topic", e.target.value)}>
          <option value="">Select a Topic</option>
          {topics.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>

      <Field id="contactMessage" label="Message" required error={f.errors.message}>
        <textarea id="contactMessage" placeholder="Tell us a little about your business and what you're looking for..."
          value={f.values.message} onChange={(e) => f.set("message", e.target.value)} />
      </Field>

      <button className="dd-btn dd-btn-primary dd-btn-block dd-btn-pill" type="submit" disabled={f.submitting}>
        {f.submitting ? "Sending…" : "Send Message →"}
      </button>
    </form>
  );
};

const DemoForm = () => {
  const f = useForm(
    { fullName: "", company: "", email: "", jobTitle: "", headcount: "", country: "", help: [] },
    {
      fullName: (v) => (!v.trim() ? "Please enter your full name." : ""),
      email: (v) => (!v.trim() ? "Please enter your email." : !EMAIL_RE.test(v) ? "Enter a valid email address." : ""),
      jobTitle: (v) => (!v.trim() ? "Please enter your job title." : ""),
      headcount: (v) => (!v ? "Please select your company headcount." : ""),
      country: (v) => (!v ? "Please select your country." : ""),
    }
  );

  const toggleHelp = (value) => {
    const has = f.values.help.includes(value);
    f.set("help", has ? f.values.help.filter((h) => h !== value) : [...f.values.help, value]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!f.validate()) {
      f.setStatus({ type: "error", msg: "Please fix the highlighted fields and try again." });
      return;
    }
    f.setSubmitting(true);
    f.setStatus(null);
    try {
      await submitDemoForm(f.values);
      f.setStatus({ type: "ok", msg: "Thanks! Your demo request has been sent. Our team will reach out soon." });
      f.reset();
    } catch (err) {
      f.setStatus({ type: "error", msg: err.message || "Something went wrong. Please try again or email info@darkdefensive.com." });
    } finally {
      f.setSubmitting(false);
    }
  };

  return (
    <form className="form-panel" noValidate onSubmit={onSubmit}>
      {f.status && (
        <div className={`form-status ${f.status.type}`} role="alert">{f.status.msg}</div>
      )}
      <div className="form-row">
        <Field id="demoName" label="Full Name" required error={f.errors.fullName}>
          <input id="demoName" type="text" placeholder="Jane Doe" autoComplete="name"
            value={f.values.fullName} onChange={(e) => f.set("fullName", e.target.value)} />
        </Field>
        <Field id="demoCompany" label="Company">
          <input id="demoCompany" type="text" placeholder="Your Organization" autoComplete="organization"
            value={f.values.company} onChange={(e) => f.set("company", e.target.value)} />
        </Field>
        <Field id="demoEmail" label="Email" required error={f.errors.email}>
          <input id="demoEmail" type="email" placeholder="you@company.com" autoComplete="email"
            value={f.values.email} onChange={(e) => f.set("email", e.target.value)} />
        </Field>
        <Field id="demoJob" label="Job Title" required error={f.errors.jobTitle}>
          <input id="demoJob" type="text" placeholder="Compliance Manager" autoComplete="organization-title"
            value={f.values.jobTitle} onChange={(e) => f.set("jobTitle", e.target.value)} />
        </Field>
      </div>

      <Field id="demoHead" label="Company Headcount" required error={f.errors.headcount}>
        <select id="demoHead" value={f.values.headcount} onChange={(e) => f.set("headcount", e.target.value)}>
          <option value="">Please Select</option>
          {headcounts.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
      </Field>

      <Field id="demoCountry" label="Country" required error={f.errors.country}>
        <select id="demoCountry" value={f.values.country} onChange={(e) => f.set("country", e.target.value)}>
          <option value="">Please Select</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>

      <fieldset>
        <legend className="fieldset-legend">How can we help?</legend>
        <div className="check-grid">
          {helpOptions.map((opt) => (
            <label className="check" key={opt}>
              <input type="checkbox" checked={f.values.help.includes(opt)} onChange={() => toggleHelp(opt)} />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <p className="consent">
        By submitting you agree to <Link to="/privacy-policy">Dark Defensive&rsquo;s Privacy Policy.</Link>
      </p>
      <button className="dd-btn dd-btn-primary dd-btn-block dd-btn-pill" type="submit" disabled={f.submitting}>
        {f.submitting ? "Sending…" : "Request Demo"}
      </button>
    </form>
  );
};

const Contact = () => {
  return (
    <div className="dd-page" data-page="contact">
      <Seo
        path="/contact-us"
        title={ROUTE_META["/contact-us"].title}
        description={ROUTE_META["/contact-us"].description}
        schema={contactSchema}
      />

      <div className="band band-net">
        <div className="wrap section">
          <div className="contact-grid">
            <div>
              <h1>Let&rsquo;s Connect</h1>
              <div className="form-shell">
                <ContactForm />
              </div>
            </div>
            <div className="contact-aside">
              <h2>
                <span className="accent">Or,</span>
                <br />request a Demo to get started!
              </h2>
              <a className="dd-btn dd-btn-primary" href="#demo-form">
                Request a Demo &gt;
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="wrap section" id="demo-form">
        <h2>Request a Demo</h2>
        <div className="form-shell">
          <DemoForm />
        </div>
      </div>
    </div>
  );
};

export default Contact;
